import type { ReactNode } from "react";
import { useEffect, useId, useRef, useState } from "react";
import { usePopoverPosition } from "../../hooks/usePopoverPosition";
import { MenuOption } from "./MenuOption";
import { MenuSection } from "./MenuSection";

export interface ContextMenuItem {
  label: string;
  icon?: ReactNode;
  /** Omit for a non-interactive row, e.g. a plain informational line like an item count. */
  onSelect?: () => void;

  selected?: boolean;
  danger?: boolean;
  disabled?: boolean;

  /** Groups consecutive items under this label, with a divider above the group. */
  group?: string;
}

/** Chunks items into runs of consecutive matching `group` values, keeping each item's real index. */
function groupItems(items: ContextMenuItem[]) {
  const groups: { label: string | undefined; entries: { item: ContextMenuItem; index: number }[] }[] = [];
  items.forEach((item, index) => {
    const last = groups[groups.length - 1];
    if (last && last.label === item.group) last.entries.push({ item, index });
    else groups.push({ label: item.group, entries: [{ item, index }] });
  });
  return groups;
}

/** Opens the menu below an element, e.g. a "..." button, so it isn't right-click only. */
export type OpenContextMenuFrom = (element: HTMLElement) => void;

export interface ContextMenuProps {
  items: ContextMenuItem[];

  /**
   * Right-clicking anywhere in it opens the menu. Pass a function to also get `openFrom` for a menu
   * button, and `isOpen` (keep that button visible while its menu is open, so focus can return to it).
   */
  children: ReactNode | ((openFrom: OpenContextMenuFrom, isOpen: boolean) => ReactNode);
  disabled?: boolean;
}

export function ContextMenu({ items, children, disabled = false }: Readonly<ContextMenuProps>) {
  const [position, setPosition] = useState<{ x: number; y: number } | null>(null);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const isOpen = position !== null;
  // Focus goes back where it was (the "..." button, the row) when the menu closes.
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const menuId = useId();
  const itemId = (index: number) => `${menuId}-item-${index}`;

  const { panelRef, style: panelStyle } = usePopoverPosition<HTMLDivElement>({
    isOpen,
    anchorPoint: position ?? undefined,
  });

  useEffect(() => {
    if (!isOpen) return;
    const handlePointerDown = (event: PointerEvent) => {
      if (!panelRef.current?.contains(event.target as Node)) setPosition(null);
    };
    const handleScroll = () => setPosition(null);
    document.addEventListener("pointerdown", handlePointerDown);
    window.addEventListener("scroll", handleScroll, true);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [isOpen, panelRef]);

  // The panel starts hidden while it's positioned, and hidden elements can't take focus: wait until it shows.
  const panelVisible = panelStyle.visibility === "visible";
  useEffect(() => {
    if (isOpen && panelVisible) panelRef.current?.focus();
  }, [isOpen, panelVisible, panelRef]);

  const close = () => {
    setPosition(null);
    returnFocusRef.current?.focus();
    returnFocusRef.current = null;
  };

  const open = (point: { x: number; y: number }) => {
    if (disabled || items.length === 0) return;
    returnFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setHighlightedIndex(0);
    setPosition(point);
  };

  const openFrom: OpenContextMenuFrom = (element) => {
    const rect = element.getBoundingClientRect();
    open({ x: rect.left, y: rect.bottom });
  };

  const commitSelection = (index: number) => {
    const item = items[index];
    if (!item || item.disabled || !item.onSelect) return;
    item.onSelect();
    close();
  };

  const handleContextMenu = (event: React.MouseEvent) => {
    if (disabled || items.length === 0) return;
    event.preventDefault();
    open({ x: event.clientX, y: event.clientY });
  };

  const handleMenuKeyDown = (event: React.KeyboardEvent) => {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setHighlightedIndex((i) => Math.min(i + 1, items.length - 1));
        break;
      case "ArrowUp":
        event.preventDefault();
        setHighlightedIndex((i) => Math.max(i - 1, 0));
        break;
      case "Home":
        event.preventDefault();
        setHighlightedIndex(0);
        break;
      case "End":
        event.preventDefault();
        setHighlightedIndex(items.length - 1);
        break;
      case "Enter":
      case " ":
        event.preventDefault();
        commitSelection(highlightedIndex);
        break;
      case "Escape":
        event.preventDefault();
        close();
        break;
    }
  };

  return (
    <>
      <div onContextMenu={handleContextMenu}>
        {typeof children === "function" ? children(openFrom, isOpen) : children}
      </div>
      {isOpen && (
        <div
          ref={panelRef}
          role="menu"
          tabIndex={-1}
          aria-activedescendant={itemId(highlightedIndex)}
          onKeyDown={handleMenuKeyDown}
          style={panelStyle}
          className="z-[110] w-48 overflow-hidden rounded-xl border border-border-input bg-surface-elevated px-1 py-1 shadow-lg outline-none"
        >
          {groupItems(items).map((group, groupIndex) => (
            <MenuSection key={group.entries[0]?.item.label} title={group.label} divider={groupIndex > 0}>
              {group.entries.map(({ item, index }) => (
                <MenuOption
                  key={item.label}
                  id={itemId(index)}
                  icon={item.icon}
                  selected={item.selected}
                  highlighted={index === highlightedIndex && !item.disabled}
                  danger={item.danger}
                  disabled={item.disabled}
                  onHighlight={() => setHighlightedIndex(index)}
                  onSelect={item.onSelect ? () => commitSelection(index) : undefined}
                >
                  {item.label}
                </MenuOption>
              ))}
            </MenuSection>
          ))}
        </div>
      )}
    </>
  );
}
